import * as types from './actionTypes';
import WebWorker from '../webWorkers/workerSetup';
import worker from '../webWorkers/worker';
import { ThunkResult } from '../models';
import { acquireToken } from '../components/auth/Auth-Utils';
import { appSyncItemNames, constants } from '../constants/constants';
import { updateVisibleProducts } from './manageInventoryActions';
import { beginAjaxCall, endAjaxCall } from './ajaxStatusActions';
import moment from 'moment';
import Queue from 'queue-promise';
import { AxiosRequestConfig } from 'axios';
import localforage from 'localforage';
import { completeCatalogDownload } from '../helpers/productSyncDiagnostics';

/*
 * Worker Actions for background processing
 * MMG-3788: Product loading now uses downloadProducts -> getPagedProducts with material/stream endpoint
 * Supporting delta downloads and weekly full refresh
 */

let myWorker: WebWorker | undefined; // since this is outside of the action function, it might be loading inside the MSAL refresh iFrame
export { myWorker };

// Track cumulative records from completed chunks for accurate progress calculation
let completedChunksRecordCount = 0;

// Track actual total products across all chunks
let actualTotalProductCount = 0;

let successfulProductChunks = 0;

// Reset product sync counters - call before starting a new download
export function resetProductSyncCounters() {
    completedChunksRecordCount = 0;
    actualTotalProductCount = 0;
    successfulProductChunks = 0;
}

// MMG-3788: Removed getAllProductsHelper and getAllProductsSuccess
// All product loading now uses downloadProducts -> getPagedProducts with material/stream endpoint
export const getJobSimpleMeasurementPointResults = (jobIDs: string[]): ThunkResult<any> => {
    return dispatch => {
        dispatch(beginAjaxCall());
        return acquireToken().then(authResponse => {
            if (!authResponse || (authResponse && !authResponse.accessToken)) {
                throw new Error('failed to retrieve token.  Likely because 3rd party cookies are disabled');
            }
            const token = authResponse.accessToken;
            if (myWorker) {
                myWorker.postMessage({
                    action: 'getSimpleJobMeasurementPointResults',
                    token,
                    jobIDs
                });
            }
        });
    };
};

const getJobSimpleMeasurementPointResultsSuccess = (data: { results: any; jobIDs: string[] }): ThunkResult<any> => {
    return dispatch => {
        dispatch({
            type: types.GET_SIMPLE_MEASUREMENT_POINT_JOB_RESULTS_SUCCESS,
            results: data.results,
            jobIDs: data.jobIDs,
            updateDate: moment().unix()
        });
    };
};

const queue = new Queue({
    concurrent: 1,
    interval: 100,
    start: false
});
queue.on('resolve', data => data);
queue.on('reject', error => console.error('[queue (reject)]:', error));

// const installBasesByID = useSelector(selectInstallBasesByID);

export const getMeasurementPointListResultsForJobsHelper = (jobId: string, facilityID: string): ThunkResult<any> => {
    return (dispatch, getState) => {
        return acquireToken().then(authResponse => {
            if (!authResponse || (authResponse && !authResponse.accessToken)) {
                throw new Error('failed to retrieve token. Likely because 3rd party cookies are disabled');
            }
            const installs = getState().manageInventory.installBasesByID;
            const installsForFacilityNumber = Object.values(installs).filter(
                install => install.facilityID === facilityID
            ).length;

            const root = process.env.REACT_APP_SERVER_DOMAIN;
            const token = authResponse.accessToken;
            const batchAmount = 250; // MPLR paging
            const numberOfRuns = Math.ceil(installsForFacilityNumber / batchAmount);

            // If the facility has no installs, we still want to dispatch the success action with an empty payload
            if (numberOfRuns === 0) {
                dispatch({
                    type: types.GET_MEASUREMENT_POINT_FACILITY_RESULTS_SUCCESS,
                    results: [],
                    facilityID,
                    updateDate: moment().unix()
                });
            } else {
                for (let i = 0; i < numberOfRuns; i++) {
                    let axiosOptions: AxiosRequestConfig = {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            Accept: 'application/json',
                            'Content-Type': 'application/json'
                        },
                        method: 'get',
                        timeout: 1000 * 60 * 5,
                        url: `${root}/MeasurementPoint/GetPagedMeasurementPointListResultsForJob?JobID=${jobId}&page=${i}&perPage=${batchAmount}&lastPage=${i ===
                            numberOfRuns - 1}`
                    };

                    dispatch({
                        type: types.GET_MEASUREMENT_POINT_FACILITY_RESULTS,
                        meta: {
                            offline: {
                                effect: { axiosOptions, message: 'Get MPLR for Job' },
                                commit: {
                                    type: types.GET_MEASUREMENT_POINT_FACILITY_RESULTS_SUCCESS,
                                    facilityID,
                                    updateDate: moment().unix()
                                }
                            }
                        }
                    });
                }
            }
        });
    };
};

const getFacilityMeasurementPointResultsSuccess = (data: { results: any; facilityID: string }): ThunkResult<any> => {
    return dispatch => {
        dispatch({
            type: types.GET_MEASUREMENT_POINT_FACILITY_RESULTS_SUCCESS,
            results: data.results,
            facilityID: data.facilityID,
            updateDate: moment().unix()
        });
        queue.dequeue(); // trigger the next item in the queue
        if (queue.size === 0) {
            dispatch(endAjaxCall());
        }
    };
};

/*
 * 3/7/24 - Don't log out the user, if an api call fails
 * TODO - Might be good to add a toast error back, if the user is online, and only recieved a network error
 * If you're offline and it fails, we must assume it's because you're offline, but that doesn't warrent a logout
 */
export function initWorker(): ThunkResult<any> {
    return (dispatch, getState) => {
        myWorker = new WebWorker(worker);
        myWorker.addEventListener('message', (event: any) => {
            switch (event.data.action) {
                case 'downloadProgress': {
                    dispatch({ type: types.SET_DOWNLOAD_SPEED, downloadSpeed: event.data.data });
                    break;
                }
                // MMG-3788: Removed 'getAllProductsSucess' handler (legacy inventory/products path)
                case 'productProcessingProgress': {
                    dispatch({
                        type: types.SET_PRODUCT_PROCESSING_PROGRESS,
                        page: event.data.chunkIndex,
                        currentBatch: event.data.currentBatch,
                        totalBatches: event.data.totalBatches,
                        productsProcessed: completedChunksRecordCount + event.data.productsProcessed,
                        totalProducts: actualTotalProductCount || event.data.totalProducts
                    });
                    break;
                }
                case 'updateProductRecordCount': {
                    // Accumulate actual total from each chunk
                    // Don't update Redux recordCount yet to avoid progress bar jumping
                    // Final total will be set when all chunks complete
                    actualTotalProductCount += event.data.totalProducts;
                    break;
                }
                case 'updateProductProgress': {
                    // Use actual cumulative records from completed chunks + current chunk progress
                    const cumulativeRecordsProcessed = completedChunksRecordCount + event.data.recordsProcessed;
                    
                    // Get current recordCount from state (initial estimate or final total)
                    const currentState = getState().syncStatus.initialAppSyncList.find(
                        item => item.name === appSyncItemNames.Products
                    );
                    const currentRecordCount = currentState?.recordCount || 50000;
                    
                    // Update progress but keep existing recordCount to avoid jumping
                    dispatch({
                        type: types.UPDATE_SYNC_ITEM_PROGRESS,
                        name: appSyncItemNames.Products,
                        recordsProcessed: cumulativeRecordsProcessed,
                        recordCount: currentRecordCount
                    });
                    break;
                }
                case 'getPagedProductsSuccess': {
                    successfulProductChunks++;
                    const previousCount = getState().syncStatus.backgroundProductSyncFinishedCount;
                    const newCount = previousCount + 1;

                    // Update cumulative total with this completed chunk's records
                    // Get the current recordsProcessed which includes this chunk
                    const currentState = getState().syncStatus.initialAppSyncList.find(
                        item => item.name === appSyncItemNames.Products
                    );
                    if (currentState) {
                        completedChunksRecordCount = currentState.recordsProcessed || 0;
                    }

                    dispatch({
                        type: types.SET_BACKGROUND_PRODUCT_SYNC_FINISHED_COUNT,
                        backgroundProductSyncFinishedCount: newCount
                    });

                    // When all 4 product calls complete, finish the sync
                    if (newCount >= 4) {
                        if (successfulProductChunks >= 4) {
                            completeCatalogDownload('full');
                            localforage.setItem('med-gas-mobile_all-products_full_sync', new Date().toISOString());
                            localforage.setItem(
                                'med-gas-mobile_all-products_full_sync_v',
                                constants.productsFullSyncVersion
                            );
                        }

                        // Final update with actual total
                        dispatch({
                            type: types.UPDATE_SYNC_ITEM_PROGRESS,
                            name: appSyncItemNames.Products,
                            recordsProcessed: actualTotalProductCount,
                            recordCount: actualTotalProductCount
                        });

                        dispatch({
                            type: types.SET_BACKGROUND_PRODUCT_SYNC_RUNNING,
                            backgroundProductSyncRunning: false
                        });

                        // Clear processing progress
                        dispatch({
                            type: types.SET_PRODUCT_PROCESSING_PROGRESS,
                            productProcessingProgress: null
                        });

                        // Remove the Products sync item - this will allow dialog to close
                        dispatch({ type: types.REMOVE_INITIAL_APP_SYNC_ITEM, name: appSyncItemNames.Products });
                        dispatch(updateVisibleProducts());
                        
                    }
                    break;
                }
                case 'getPagedProductsFailed': {
                    const newCount = getState().syncStatus.backgroundProductSyncFinishedCount + 1;
                    
                    dispatch({
                        type: types.SET_BACKGROUND_PRODUCT_SYNC_FINISHED_COUNT,
                        backgroundProductSyncFinishedCount: newCount
                    });

                    // If all 4 chunks have completed (success or fail), finish the sync
                    if (newCount >= 4) {
                        dispatch({
                            type: types.SET_BACKGROUND_PRODUCT_SYNC_FAILED,
                            backgroundProductSyncFailed: true
                        });
                        dispatch({
                            type: types.SET_BACKGROUND_PRODUCT_SYNC_RUNNING,
                            backgroundProductSyncRunning: false
                        });

                        // Clear processing progress
                        dispatch({
                            type: types.SET_PRODUCT_PROCESSING_PROGRESS,
                            productProcessingProgress: null
                        });

                        dispatch({ type: types.REMOVE_INITIAL_APP_SYNC_ITEM, name: appSyncItemNames.Products });
                        // Still try to load whatever products we got
                        dispatch(updateVisibleProducts());
                    }
                    break;
                }
                case 'getResultsSuccess': {
                    dispatch(getFacilityMeasurementPointResultsSuccess(event.data));
                    break;
                }
                case 'getResultsFailed': {
                    dispatch(endAjaxCall());
                    console.error('[initWorker -> getResultsFailed]:', event, event.data);
                    // if (errorCount < 1) {
                    //     toastr.error(
                    //         'Error',
                    //         'Downloading hospital data failed.  Logging out... Please login and try again or contact support.',
                    //         constants.toastrError
                    //     );
                    //     errorCount++;
                    // }
                    // setTimeout(() => {
                    //     document.dispatchEvent(new CustomEvent('startUserLogoutSessionOnly'));
                    //     errorCount = 0;
                    // }, 120000);
                    break;
                }
                case 'getSimpleResultsSuccess': {
                    dispatch(getJobSimpleMeasurementPointResultsSuccess(event.data));
                    break;
                }
                case 'getSimpleResultsFailed': {
                    dispatch(endAjaxCall());
                    console.error('[initWorker -> getSimpleResultsFailed]:', event, event.data);
                    // if (errorCount < 1) {
                    //     toastr.error(
                    //         'Error',
                    //         'Downloading hospital data failed.  Logging out... Please login and try again or contact support.',
                    //         constants.toastrError
                    //     );
                    //     errorCount++;
                    // }
                    // setTimeout(() => {
                    //     document.dispatchEvent(new CustomEvent('startUserLogoutSessionOnly'));
                    //     errorCount = 0;
                    // }, 120000);
                    break;
                }
                // MMG-3788: Removed 'getAllProductsFailed' handler (legacy inventory/products path)

                default:
                    break;
            }
        });

        // error initializing the worker
        myWorker.addEventListener('error', (event: any) => {
            console.error('[initWorker]: recieved error', event);
        });
    };
}
